import { useSearchParams } from 'react-router-dom';
import { Alert } from '../../components/ui.tsx';
import AdminVerifyForm from '../../components/admin/verify-form.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';

/**
 * Operator sign-in: one button, straight to the Google leg. There is no
 * password path and no signup — access is the allowlist, managed inside the
 * CMS itself.
 */

const ERROR_MESSAGES: Record<string, string> = {
  not_enabled: 'Google sign-in is not configured on this deployment.',
  declined: 'The sign-in was canceled at Google.',
  expired: 'That sign-in attempt expired. Please try again.',
  state: 'That sign-in could not be matched to this browser. Please try again.',
  provider: 'Google could not complete the sign-in. Please try again.',
  denied: 'That Google account does not have admin access.',
  rate_limited: 'Too many attempts. Please wait a few minutes and try again.',
  server_error: 'Something went wrong on our end. Please try again.',
};

export default function AdminLoginPage() {
  const [searchParams] = useSearchParams();
  // The OAuth callback sends an operator back here with `verify=1` when the
  // mailbox checked out but a second factor is still owed. Without this branch
  // that redirect landed on the Google button again, and following it simply
  // repeated the callback — an MFA-enabled operator could never finish signing
  // in at all.
  const verifying = searchParams.get('verify') === '1';
  useDocumentTitle(verifying ? 'Verify it’s you' : 'Admin sign in');
  const errorCode = searchParams.get('error');
  const error = errorCode ? (ERROR_MESSAGES[errorCode] ?? ERROR_MESSAGES.server_error) : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--color-surface)] px-4 py-12">
      <main className="w-full max-w-md">
        <img src="/BlastoClean-Logo.svg" alt="BlastoClean" className="mx-auto mb-6 h-10 w-auto" />
        <div className="card">
          <h1 className="text-2xl font-bold">Admin console</h1>

          {verifying ? (
            // A hard navigation rather than a client-side route: the verify
            // call is what mints the session cookies, and the console's own
            // loaders must run against a document that already has them.
            <div className="mt-4">
              <AdminVerifyForm onVerified={() => window.location.assign('/admin')} />
            </div>
          ) : (
            <>
              <p className="mb-6 mt-1 text-[color:var(--color-muted)]">
                Sign in with the Google account on the admin list.
              </p>

              {error ? (
                <div className="mb-4">
                  <Alert tone="error">{error}</Alert>
                </div>
              ) : null}

              {/* A plain navigation, exactly like the tenant social buttons:
                  the redirect to Google is built server-side. */}
              <a
                href="/api/admin/auth/google/start"
                className="flex w-full items-center justify-center gap-3 rounded-md border border-[color:var(--color-line)] bg-white px-4 py-2.5 font-medium hover:bg-[color:var(--color-surface)]"
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5">
                  <path
                    fill="#4285F4"
                    d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.46a5.52 5.52 0 0 1-2.4 3.62v3h3.87c2.27-2.09 3.57-5.17 3.57-8.81Z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.28v3.1A12 12 0 0 0 12 24Z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.27 14.28A7.2 7.2 0 0 1 4.9 12c0-.79.14-1.56.37-2.28v-3.1H1.28a12 12 0 0 0 0 10.76l3.99-3.1Z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 4.77c1.76 0 3.34.6 4.59 1.79l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.28 6.62l3.99 3.1C6.22 6.88 8.87 4.77 12 4.77Z"
                  />
                </svg>
                Sign in with Google
              </a>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
